import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import { getPredictedPhotos } from '../../services/photoService';
import type { Photo } from '../../types/photo';
import HeatmapLayer from './HeatmapLayer';

const HomePageMap = () => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchMarkers = async () => {
      try {
        const data = await getPredictedPhotos();
        setPhotos(data);
      } catch (error) {
        console.error("Failed to fetch koala locations:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarkers();
  }, []);

  const heatmapPoints: [number, number, number?][] = photos
    .filter(p => p.latitudeDecValue && p.longitudeDecValue)
    .map(p => [p.latitudeDecValue, p.longitudeDecValue, 1]);

  return (
    <section className="custom-section">
      <div className="max-w-6xl mx-auto">
        <div className="flex w-full md:flex-row flex-col">
          <div className="md:w-2/3 w-full h-[400px] inline-flex rounded-xl overflow-hidden shadow-md border border-gray-200">
            {loading ? (
              <div className="flex items-center justify-center w-full h-full text-gray-400">
                Loading map...
              </div>
            ) : (
              <MapContainer
                center={[-34.93, 138.61]}
                zoom={10}
                scrollWheelZoom={false}
                
                style={{ height: '100%', width: '100%', zIndex: 0}}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution="&copy; OpenStreetMap contributors"
                />
                <HeatmapLayer points={heatmapPoints} />
              </MapContainer>
            )}
          </div>

          <div className="md:w-1/3 h-full px-6 py-6 inline-flex flex-col justify-center text-gray-900">
            <h2 className="text-4xl font-extrabold mb-4">Koala Heatmap</h2>
            <p className="text-lg text-gray-700 font-normal leading-relaxed">
              This heatmap highlights areas where koala photos were submitted. Colors show photo density and potential koala activity:
            </p>

            <ul className="mt-4 space-y-2">
              <li className="flex items-center">
                <span className="inline-block w-5 h-5 rounded-sm mr-3" style={{ backgroundColor: 'blue' }}></span>
                <span className="font-semibold text-gray-700">Low koala activity</span>
              </li>
              <li className="flex items-center">
                <span className="inline-block w-5 h-5 rounded-sm mr-3" style={{ backgroundColor: 'cyan' }}></span>
                <span className="font-semibold text-gray-700">Mild activity</span>
              </li>
              <li className="flex items-center">
                <span className="inline-block w-5 h-5 rounded-sm mr-3" style={{ backgroundColor: 'lime' }}></span>
                <span className="font-semibold text-gray-700">Moderate activity</span>
              </li>
              <li className="flex items-center">
                <span className="inline-block w-5 h-5 rounded-sm mr-3" style={{ backgroundColor: 'orange' }}></span>
                <span className="font-semibold text-gray-700">High activity</span>
              </li>
              <li className="flex items-center">
                <span className="inline-block w-5 h-5 rounded-sm mr-3" style={{ backgroundColor: 'red' }}></span>
                <span className="font-semibold text-gray-700">Hotspot – frequent sightings</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HomePageMap;
